import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { SRLevel, SRStatus } from '@contracts/sr';
import { api } from '../lib/api';
import { relative } from '../lib/format';
import { signalPrice } from '../lib/notify';
import { useSR } from '../store/sr';
import { Empty, Panel } from './Panel';
import { DirectionChip } from './SignalPanel';

function Nearest({ label, lv, sign }: { label: string; lv: SRLevel | undefined; sign: string }) {
  return (
    <div className="level">
      <div className="level__label">{label}</div>
      <div className="level__value tabular">{lv ? `${signalPrice(lv.lower)} – ${signalPrice(lv.upper)}` : '—'}</div>
      {lv ? (
        <div className="small muted">
          {'★'.repeat(lv.stars)} · {sign}${lv.distance.toFixed(1)}
        </div>
      ) : null}
    </div>
  );
}

/** Dashboard summary of the separate support/resistance system; details on /sr. */
export function SRPanel() {
  const live = useSR((s) => s.status);
  const q = useQuery({
    queryKey: ['sr-status'],
    queryFn: () => api.get<SRStatus>('/api/v1/sr/status'),
    refetchInterval: 60_000,
    enabled: live === null,
  });
  const status = live ?? q.data ?? null;
  const a = status?.openAlert ?? null;
  return (
    <Panel
      id="sr"
      title="สัญญาณแนวรับแนวต้าน"
      meta={status?.evaluation?.evaluatedAt ? `H1 ตรวจ ${relative(status.evaluation.evaluatedAt)}` : undefined}
    >
      {!status ? (
        <Empty title={q.isError ? 'โหลดไม่สำเร็จ' : 'กำลังโหลด…'} />
      ) : !status.enabled ? (
        <Empty title="ระบบแนวรับแนวต้านปิดอยู่" />
      ) : (
        <>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            {a ? (
              <span className="row">
                <DirectionChip direction={a.direction} />
                <span className="small tabular">
                  เข้า {signalPrice(a.entry)} · SL {signalPrice(a.stopLoss)} · TP {signalPrice(a.takeProfit)}
                </span>
              </span>
            ) : (
              <DirectionChip direction="WAIT" />
            )}
            <span className="small">
              ราคา <strong className="tabular">{signalPrice(status.price)}</strong> · ATR ${status.atr.toFixed(1)}
            </span>
          </div>
          <div className="grid grid--2" aria-label="แนวใกล้ราคาที่สุด" style={{ marginTop: 8 }}>
            <Nearest label="แนวต้านใกล้สุด (R1)" lv={status.levels?.resistance[0]} sign="+" />
            <Nearest label="แนวรับใกล้สุด (S1)" lv={status.levels?.support[0]} sign="−" />
          </div>
          <p className="small" style={{ marginBottom: 0 }}>
            <Link to="/sr">ดูแนวทั้งหมด แจ้งเตือน และประวัติ →</Link>
          </p>
        </>
      )}
    </Panel>
  );
}
